<script setup lang="ts">
import { computed } from "vue";

import type { ProgressCircleProps } from "@/dsComponents/progress/type";

const SVG_SIZE = 14;
const CIRCLE_RADIUS = SVG_SIZE / 2;
const STROKE_WIDTH = 2.4;
const RADIUS = (SVG_SIZE - STROKE_WIDTH) / 2;
const CIRCUMFERENCE = 2 * Math.PI * RADIUS;

const props = withDefaults(defineProps<ProgressCircleProps>(), {
    color: "primary",
});

const strokeDashoffset = computed(() => CIRCUMFERENCE - (CIRCUMFERENCE * props.fill) / 100);
</script>
<template>
    <div class="progress-circle-container">
        <div v-if="labelLeft" class="labelLeft typo-xs-regular" :style="{ color: labelColor, fontWeight: labelFontWeight }">
            {{ labelLeft }}
        </div>
        <div :class="['progress-circle', color, { checked }, { badge }]">
            <svg v-if="!checked" :width="SVG_SIZE" :height="SVG_SIZE" :viewBox="`0 0 ${SVG_SIZE} ${SVG_SIZE}`">
                <circle :cx="CIRCLE_RADIUS" :cy="CIRCLE_RADIUS" :r="RADIUS"></circle>
                <circle :cx="CIRCLE_RADIUS" :cy="CIRCLE_RADIUS" :r="RADIUS" :style="{ strokeDasharray: CIRCUMFERENCE, strokeDashoffset }"></circle>
            </svg>
            <div v-else class="checked-progress-circle">
                <i v-if="checked" class="ti ti-check"></i>
            </div>
        </div>
        <div v-if="labelRight" class="labelRight typo-xs-regular" :style="{ color: labelColor, fontWeight: labelFontWeight }">
            {{ labelRight }}
        </div>
    </div>
</template>
<style scoped lang="scss">
.progress-circle-container {
    display: flex;
    align-items: center;
    gap: var(--spacing-sm, 6px);

    .labelRight,
    .labelLeft {
        color: var(--text-main-900, #00061a);
    }

    .progress-circle {
        $size: 14px;
        @include square($size);

        svg {
            transform: rotate(-90deg);
        }

        svg circle {
            @include square($size);
            fill: none;
            stroke: var(--vibrant-blue-blue-200-light, #d6dfffff);
            stroke-width: 2.4;
            stroke-linecap: round;

            &:last-of-type {
                stroke: var(--vibrant-blue-blue-700-base, #2051f8ff);
            }
        }

        &.neutral {
            svg circle {
                stroke: var(--stroke-soft-200, #dbdfe6ff);
                &:last-of-type {
                    stroke: var(--stroke-strong-900, #00061aff);
                }
            }

            &.checked {
                .checked-progress-circle {
                    background-color: var(--bg-strong-900, #00061aff);
                }
            }

            &.badge {
                background-color: var(--bg-weak-100, #eff2f5ff);
            }
        }

        &.checked {
            .checked-progress-circle {
                height: $size;
                width: $size;
                background-color: var(--vibrant-blue-blue-700-base, #2051f8ff);
                border-radius: 9999px;
                display: flex;
                justify-content: center;
                align-items: center;
                i {
                    color: var(--text-white-0, #ffffffff);
                    line-height: 10px;
                    font-size: 10px;
                }
            }
        }

        &.badge {
            background-color: var(--vibrant-blue-blue-100-lighter, #ebefffff);
            padding: var(--spacing-xs, 4px);
            border-radius: 9999px;
            height: calc(#{$size} + var(--spacing-md, 8px));
            width: calc(#{$size} + var(--spacing-md, 8px));
        }
    }
}
</style>
